import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, RotateCcw, Search, Loader2, AlertCircle, Package, ChevronLeft, ChevronRight, ArrowLeft } from 'lucide-react';
import { Product } from '../types';
import { adminVendorProductApi, productCategoryApi } from '../services/api';
import AdminVendorProductForm from './AdminVendorProductForm';
import { extractApiError } from '../utils/errorUtils';

export default function AdminVendorProductManagement() {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const vendorIdNum = vendorId ? parseInt(vendorId) : 0;

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [categories, setCategories] = useState<{ id: number; name: string; emoji: string }[]>([]);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [restoreConfirm, setRestoreConfirm] = useState<number | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [vendorName, setVendorName] = useState<string>('');

  // Form modal state
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const pageSize = 20;

  // Load categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await productCategoryApi.getCategories();
        setCategories(data);
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('Failed to load categories:', apiError);
      }
    };
    fetchCategories();
  }, []);

  const loadProducts = useCallback(async () => {
    if (!vendorIdNum) return;

    try {
      setLoading(true);
      setError(null);
      const response = await adminVendorProductApi.getVendorProducts(
        vendorIdNum,
        currentPage,
        pageSize,
        selectedCategory || undefined,
        includeDeleted
      );
      setProducts(response.items);
      setTotalPages(response.total_pages);
      setTotalProducts(response.total);

      // Get vendor name from first product if available
      if (response.items.length > 0 && !vendorName) {
        // Vendor name would need to be fetched separately
        // For now, just use the vendor ID
        setVendorName(`Vendor #${vendorIdNum}`);
      }
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load products:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  }, [vendorIdNum, currentPage, pageSize, selectedCategory, includeDeleted, vendorName]);

  // Load products when filters change
  useEffect(() => {
    if (vendorIdNum) {
      loadProducts();
    }
  }, [vendorIdNum, loadProducts]);

  const handleDelete = async (productId: number) => {
    if (!vendorIdNum) return;

    try {
      setIsDeleting(true);
      await adminVendorProductApi.deleteVendorProduct(vendorIdNum, productId);
      setDeleteConfirm(null);
      loadProducts(); // Reload products
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to delete product:', apiError);
      setError(apiError.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRestore = async (productId: number) => {
    if (!vendorIdNum) return;

    try {
      setIsRestoring(true);
      await adminVendorProductApi.restoreVendorProduct(vendorIdNum, productId);
      setRestoreConfirm(null);
      loadProducts(); // Reload products
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to restore product:', apiError);
      setError(apiError.message);
    } finally {
      setIsRestoring(false);
    }
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditingProduct(null);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingProduct(null);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingProduct(null);
    loadProducts();
  };

  // Filter products by search term (client-side for current page)
  const filteredProducts = products.filter(product => {
    const matchesSearch = searchTerm === '' ||
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.variety && product.variety.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  if (!vendorIdNum) {
    return (
      <div className="min-h-screen bg-gray-50 p-4" data-testid="admin-vendor-products-invalid">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800" data-testid="invalid-vendor-message">Invalid vendor ID</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4" data-testid="admin-vendor-products">
      <div className="max-w-7xl mx-auto">
        {/* Header with Breadcrumb */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/vendors')}
            data-testid="back-to-vendors-button"
            className="flex items-center text-secondary hover:text-primary-dark mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Vendors
          </button>
          <h1 className="text-3xl font-bold text-gray-900" data-testid="vendor-products-title">
            <Package className="inline h-8 w-8 mr-2 text-secondary" />
            Vendor Products Management
          </h1>
          <p className="text-gray-600 mt-1" data-testid="vendor-name">{vendorName}</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6" data-testid="product-filters">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                data-testid="product-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <select
              value={selectedCategory}
              data-testid="product-category-select"
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.name}>
                  {cat.emoji} {cat.name}
                </option>
              ))}
            </select>

            {/* Include Deleted Checkbox */}
            <label className="flex items-center px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-pointer hover:bg-gray-100">
              <input
                type="checkbox"
                data-testid="include-deleted-checkbox"
                checked={includeDeleted}
                onChange={(e) => {
                  setIncludeDeleted(e.target.checked);
                  setCurrentPage(1);
                }}
                className="mr-2 h-4 w-4 text-secondary rounded focus:ring-primary"
              />
              <span className="text-sm text-gray-700">Include Deleted</span>
            </label>

            {/* Create Button */}
            <button
              onClick={handleCreate}
              data-testid="create-product-button"
              className="flex items-center justify-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create Product
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start" data-testid="products-error-message">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
            <div>
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Products Table/List */}
        {loading ? (
          <div className="flex justify-center items-center py-12" data-testid="products-loading">
            <Loader2 className="h-8 w-8 text-secondary animate-spin" />
            <span className="ml-2 text-gray-600">Loading products...</span>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center" data-testid="products-empty-state">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No products found</p>
            {(searchTerm || selectedCategory) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('');
                }}
                data-testid="clear-filters-button"
                className="mt-4 text-secondary hover:text-primary-dark"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block bg-white rounded-lg shadow-md overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200" data-testid="products-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredProducts.map(product => (
                    <tr key={product.id} className={product.deleted_at ? 'bg-gray-50 opacity-60' : ''} data-testid="product-row">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          {product.emoji && <span className="text-2xl mr-3">{product.emoji}</span>}
                          <div>
                            <div className="text-sm font-medium text-gray-900">{product.name}</div>
                            {product.variety && (
                              <div className="text-sm text-gray-500">{product.variety}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {product.category}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {product.deleted_at ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                            Deleted
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                            Active
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          {product.deleted_at ? (
                            restoreConfirm === product.id ? (
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleRestore(product.id)}
                                  disabled={isRestoring}
                                  data-testid="confirm-restore-button"
                                  className="text-green-600 hover:text-green-900 disabled:opacity-50"
                                >
                                  {isRestoring ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm'}
                                </button>
                                <button
                                  onClick={() => setRestoreConfirm(null)}
                                  data-testid="cancel-restore-button"
                                  className="text-gray-600 hover:text-gray-900"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setRestoreConfirm(product.id)}
                                data-testid="restore-product-button"
                                className="text-green-600 hover:text-green-900 flex items-center"
                              >
                                <RotateCcw className="h-4 w-4 mr-1" />
                                Restore
                              </button>
                            )
                          ) : (
                            <>
                              <button
                                onClick={() => handleEdit(product)}
                                data-testid="edit-product-button"
                                className="text-secondary hover:text-teal-900 flex items-center"
                              >
                                <Edit className="h-4 w-4 mr-1" />
                                Edit
                              </button>
                              {deleteConfirm === product.id ? (
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => handleDelete(product.id)}
                                    disabled={isDeleting}
                                    data-testid="confirm-delete-product-button"
                                    className="text-red-600 hover:text-red-900 disabled:opacity-50"
                                  >
                                    {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm'}
                                  </button>
                                  <button
                                    onClick={() => setDeleteConfirm(null)}
                                    data-testid="cancel-delete-product-button"
                                    className="text-gray-600 hover:text-gray-900"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setDeleteConfirm(product.id)}
                                  data-testid="delete-product-button"
                                  className="text-red-600 hover:text-red-900 flex items-center"
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Delete
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
              {filteredProducts.map(product => (
                <div
                  key={product.id}
                  data-testid="product-card"
                  className={`bg-white rounded-lg shadow-md p-4 ${product.deleted_at ? 'opacity-60 bg-gray-50' : ''}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center">
                      {product.emoji && <span className="text-3xl mr-3">{product.emoji}</span>}
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{product.name}</h3>
                        {product.variety && (
                          <p className="text-sm text-gray-500">{product.variety}</p>
                        )}
                      </div>
                    </div>
                    {product.deleted_at ? (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                        Deleted
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                        Active
                      </span>
                    )}
                  </div>

                  <div className="mb-3">
                    <span className="text-sm text-gray-600">Category: </span>
                    <span className="text-sm font-medium text-gray-900">{product.category}</span>
                  </div>

                  <div className="flex space-x-2">
                    {product.deleted_at ? (
                      restoreConfirm === product.id ? (
                        <>
                          <button
                            onClick={() => handleRestore(product.id)}
                            disabled={isRestoring}
                            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                          >
                            {isRestoring ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Confirm Restore'}
                          </button>
                          <button
                            onClick={() => setRestoreConfirm(null)}
                            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setRestoreConfirm(product.id)}
                          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center"
                        >
                          <RotateCcw className="h-4 w-4 mr-2" />
                          Restore
                        </button>
                      )
                    ) : (
                      <>
                        <button
                          onClick={() => handleEdit(product)}
                          className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark flex items-center justify-center"
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </button>
                        {deleteConfirm === product.id ? (
                          <>
                            <button
                              onClick={() => handleDelete(product.id)}
                              disabled={isDeleting}
                              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                            >
                              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Confirm'}
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(product.id)}
                            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between" data-testid="products-pagination">
                <div className="text-sm text-gray-700" data-testid="products-count">
                  Showing {filteredProducts.length} of {totalProducts} products
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    data-testid="previous-page-button"
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </button>
                  <span className="px-4 py-2 text-sm text-gray-700" data-testid="current-page">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    data-testid="next-page-button"
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <AdminVendorProductForm
          vendorId={vendorIdNum}
          product={editingProduct}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
}
